import React from 'react';
import { Link, useLocation, useParams } from 'react-router-dom';
import { ThreeDots } from 'react-loader-spinner';

import { useFetchLessons } from '../../hooks/useFetchLessons';
import { SUBJECTS } from '../../constants/routes';
import { CustomCarousel } from '../CustomCarousel/CustomCarousel';

import './lesson.css';

export const Lesson: React.FC<React.PropsWithChildren<{}>> = ({ children }) => {
  const location = useLocation();
  const { numOfLesson, dayNumber } = useParams<{
    numOfLesson: string;
    dayNumber: string;
  }>();

  const currentGrade = location.pathname.includes('grade-11') ? 11 : 10;
  const currentSubject = location.pathname.includes('medicine')
    ? SUBJECTS.MEDICINE
    : SUBJECTS.NATIONAL_DEFENSE;

  const { currentLesson, isLoading } = useFetchLessons({
    dayNumber: dayNumber || '',
    lessonNumber: numOfLesson || '',
    subjectName: currentSubject,
    classNumber: currentGrade,
  });

  const prevLesson = Math.max(1, parseInt(numOfLesson!) - 1);
  const nextLesson = Math.min(8, parseInt(numOfLesson!) + 1);
  
  // Build the URL by replacing the lesson number in the current path
  const prevUrl = location.pathname.replace(
    `/lessons/${numOfLesson}`,
    `/lessons/${prevLesson}`
  );
  const nextUrl = location.pathname.replace(
    `/lessons/${numOfLesson}`,
    `/lessons/${nextLesson}`
  );

  const isPrevDisabled = parseInt(numOfLesson!) === 1;
  const isNextDisabled = parseInt(numOfLesson!) === 8;

  const lessonsNavigation = (
    <nav className="lesson-header" aria-label="Навігація по урокам">
      <div>
        {!isPrevDisabled ? (
          <Link to={prevUrl} aria-label="Попередній урок">
            ← Попередній урок
          </Link>
        ) : (
          <span style={{ opacity: 0.5, cursor: 'not-allowed' }}>
            ← Попередній урок
          </span>
        )}
      </div>
      <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>
        День {dayNumber} • Урок {numOfLesson}
      </div>
      <div>
        {!isNextDisabled ? (
          <Link to={nextUrl} aria-label="Наступний урок">
            Наступний урок →
          </Link>
        ) : (
          <span style={{ opacity: 0.5, cursor: 'not-allowed' }}>
            Наступний урок →
          </span>
        )}
      </div>
    </nav>
  );

  if (isLoading) {
    return (
      <>
        {lessonsNavigation}
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          alignItems: 'center', 
          gap: '1rem',
          marginTop: '3rem' 
        }}>
          <ThreeDots color="#4da6ff" height={80} width={80} />
          <p style={{ color: '#4da6ff', fontSize: '1.2rem' }}>
            Завантаження уроку...
          </p>
        </div>
      </>
    );
  }

  if (!currentLesson) {
    return (
      <>
        {lessonsNavigation}
        <div style={{
          padding: '3rem',
          background: 'rgba(255, 193, 7, 0.1)',
          borderRadius: '12px',
          border: '1px solid rgba(255, 193, 7, 0.3)',
          marginTop: '2rem',
        }}>
          <p style={{ fontSize: '1.3rem', color: '#ffc107' }}>
            ⚠️ Урок ще не додано
          </p>
        </div>
      </>
    );
  }

  const {
    title,
    books,
    videos,
    presentationLink,
    test,
    homeWorks,
    textSections,
  } = currentLesson;

  return (
    <>
      {lessonsNavigation}

      <h1 className="lesson-title">{title}</h1>

      {books.length > 0 && (
        <section className="lesson-books" aria-label="Підручники">
          {books.map((source, i) => (
            <div className="source-book" key={`${source.name}-${i}`}>
              <span>Підручник {i + 1}: </span>
              <a href={source.link} target="_blank" rel="noopener noreferrer">
                {source.name}
              </a>
            </div>
          ))}
        </section>
      )}
      
      {videos.length !== 0 && (
        <section className="lesson-video" aria-label="Відео матеріали">
          <div>📹 Посилання на відео до уроку:</div>
          <CustomCarousel videos={videos} />
        </section>
      )}

      {presentationLink.length > 0 && (
        <section className="lesson-presentation" aria-label="Презентація уроку">
          {presentationLink.map((presentation, index) => (
            <iframe
              src={`${presentation.link}/embed?start=true&loop=false&delayms=20000`}
              key={`${presentation.name}-${index}`}
              title={`${title} - Презентація ${index + 1}`}
              frameBorder="0"
              width="680"
              height="524"
              allowFullScreen={true}
              loading="lazy"
            >
              Ваш браузер не підтримує можливості перегляду презентації
            </iframe>
          ))}
        </section>
      )}

      <article className="main-text">
        {textSections.map((slide, slideIndex) => {
          const paragraphs = slide.mainText ? slide.mainText.split(/\n/) : [''];

          return (
            <section key={`${slide.title}-${slideIndex}`}>
              <h2 className="item-title">{slide.title}</h2>
              <div className="item-text">
                {paragraphs.map((paragraph, pIndex) => (
                  <p key={`${paragraph}-${pIndex}`}>{paragraph}</p>
                ))}
              </div>
              {(slide.images?.length || slide.imagesAlternative?.length) && (
                <div className="item-image">
                  {slide.images?.length
                    ? slide.images.map((image) => (
                        <figure key={image.id} className="image-card">
                          <div className="image-card-item">
                            <img
                              src={`${process.env.REACT_APP_HOST_URL}${image.url}`}
                              alt={image.alternativeText || image.name}
                              loading="lazy"
                            />
                          </div>
                          <figcaption className="image-card-title">{image.name}</figcaption>
                        </figure>
                      ))
                    : slide.imagesAlternative?.map((image) => (
                        <figure key={image.id} className="image-card">
                          <div className="image-card-item">
                            <img src={image.base64} alt={image.name} loading="lazy" />
                          </div>
                          <figcaption className="image-card-title">{image.name}</figcaption>
                        </figure>
                      ))}
                </div>
              )}
              <hr />
            </section>
          );
        })}
      </article>

      {test.length > 0 && (
        <section className="lesson-test" aria-label="Тести">
          {test.map((t, index) => (
            <div key={`${index}-${t.name}`}>
              <a href={t.link} target="_blank" rel="noopener noreferrer" aria-label={`Відкрити тест: ${t.name}`}>
                ✅ {t.name}
              </a>
            </div>
          ))}
        </section>
      )}

      {homeWorks.length !== 0 && (
        <section className="lesson-homework" aria-label="Домашнє завдання">
          <h2 className="homework-title">📝 Домашнє завдання:</h2>
          {homeWorks.map((hw, i) => (
            <div className="homework-item" key={`${hw.name}-${i}`}>
              <a href={hw.link} target="_blank" rel="noopener noreferrer" aria-label={`Завантажити: ${hw.name}`}>
                {hw.name}
              </a>
            </div>
          ))}
        </section>
      )}
    </>
  );
};
